import { Plus } from "lucide-react";
import * as React from "react";
import { Link, useNavigate } from "react-router";

import { useCurrentOrg, useHasOrgRole } from "@/providers/auth/hooks";
import { ExplainedBadge } from "@/shared/components/ExplainedBadge";
import {
  FormattedDateTime,
  formatRelativeTime,
  isRecent,
} from "@/shared/datetime";
import {
  IngestionLabel,
  IngestionStatus,
  ingestionLabel,
  type FileRecord,
} from "@/shared/domain/files";
import {
  fileExtension,
  filePatternLabel,
  rulePrefillForFile,
  type DefaultIngestionRule,
  INGEST_FILE_OUTCOME_MESSAGES,
  type IngestionRulePrefill,
  type IngestionRuleRecord,
  type IngestionRun,
} from "@/shared/domain/ingestionRules";
import {
  useDefaultIngestionRules,
  useIngestFiles,
  useIngestionRulesMatching,
} from "@/shared/domain/ingestionRules/hooks";
import {
  RobotoButton,
  Spinner,
  type BadgeVariant,
} from "@/shared/foundation/primitives";
import {
  RobotoUnauthorizedException,
  userFacingErrorMessage,
} from "@/shared/http";
import { Goto } from "@/shared/navigation";

import styles from "./IngestionLabelBadge.module.css";
import {
  FIRST_RUN_GRACE_MS,
  mayHaveRun,
  useIsOrgMember,
  useProvidedIngestionRun,
} from "./latestIngestionRuns";
import { IngestionRunLookup } from "./LatestIngestionRunsProvider";

/** Red only for a run that failed; a file part way through is not an error. */
const VARIANTS: Record<IngestionLabel, BadgeVariant> = {
  [IngestionLabel.Ingested]: "success",
  [IngestionLabel.Ingesting]: "info",
  [IngestionLabel.PartlyIngested]: "warning",
  [IngestionLabel.IngestionFailed]: "error",
  [IngestionLabel.AwaitingIngestion]: "warning",
  [IngestionLabel.NotWaitedFor]: "secondary",
  [IngestionLabel.StoredAsIs]: "secondary",
};

const INGESTED_MEANS =
  "Ingested means its signals can be plotted and searched.";
const PARTLY_INGESTED =
  "Some of this file’s signals are ingested and some are missing.";

type LabelledFile = Pick<
  FileRecord,
  "ingestion_status" | "ingestable" | "relative_path" | "org_id"
> &
  Partial<Pick<FileRecord, "file_id" | "created">>;

export interface WaitSkipped {
  skippedAt: string | null;
  skippedBy: string | null;
}

export interface IngestionLabelBadgeProps {
  /**
   * Without `file_id` the badge cannot look up the file's runs or ingest it;
   * with `created` its popup says when the file was uploaded.
   */
  file: LabelledFile;
  /** Places the label within its container, e.g. pushed to the end of a table cell. */
  className?: string;
  /**
   * The file's latest ingestion run, or null when it has none. Undefined
   * means unknown: the badge reads it from a `LatestIngestionRunsProvider`,
   * or looks it up itself when its popup opens.
   */
  latestRun?: IngestionRun | null;
  /** The session the file is in stopped waiting for it. */
  waitSkipped?: WaitSkipped;
}

/**
 * A file's ingestion state as one coloured label. Clicking it opens a popup
 * that explains the state and offers what can be done about it: ingest the
 * file now, run a failed ingestion again, or add a rule for files like it.
 * Hovering does not open it, so a table of these stays quiet.
 */
export function IngestionLabelBadge({
  file,
  className,
  latestRun,
  waitSkipped,
}: IngestionLabelBadgeProps) {
  const [opened, setOpened] = React.useState(false);
  const provided = useProvidedIngestionRun(
    latestRun === undefined ? file.file_id : undefined,
  );
  const [lookedUp, setLookedUp] = React.useState<IngestionRun | null>();
  const known = latestRun !== undefined ? latestRun : provided?.run;
  const run = known !== undefined ? known : lookedUp;
  const runContext = {
    latestRun: run,
    recordPredatesRunEnd: provided?.recordPredatesRunEnd,
  };
  const label = ingestionLabel(file, {
    ...runContext,
    waitSkipped: waitSkipped !== undefined,
  });
  return (
    <>
      <ExplainedBadge
        label={label}
        variant={VARIANTS[label]}
        aria-label={`${label}: why?`}
        className={className}
        openOnHover={false}
        onOpenChange={(open) => {
          if (open) {
            setOpened(true);
          }
        }}
        explanation={
          <Explanation
            file={file}
            label={label}
            stateLabel={ingestionLabel(file, runContext)}
            run={run ?? null}
            runKnown={run !== undefined}
            waitSkipped={waitSkipped}
          />
        }
        data-testid="ingestion-label"
      />
      {opened && known === undefined && mayHaveRun(file) && (
        <IngestionRunLookup file={file} onRun={setLookedUp} />
      )}
    </>
  );
}

/**
 * The popup's body. One component for every label, so an ingest started here
 * keeps its outcome on screen while the label changes under it.
 */
function Explanation({
  file,
  label,
  stateLabel,
  run,
  runKnown,
  waitSkipped,
}: {
  file: LabelledFile;
  label: IngestionLabel;
  /** The label the file would have if no session had stopped waiting for it. */
  stateLabel: IngestionLabel;
  run: IngestionRun | null;
  /** Whether ``run`` is known, so a null one means no run ever started. */
  runKnown: boolean;
  waitSkipped: WaitSkipped | undefined;
}) {
  const ingest = useIngestFiles(file.org_id);
  return (
    <>
      {waitSkipped !== undefined && label === IngestionLabel.NotWaitedFor && (
        <p data-testid="ingestion-label-skipped">
          The session stopped waiting for this file
          {waitSkipped.skippedBy !== null && ` (${waitSkipped.skippedBy})`}
          {waitSkipped.skippedAt !== null && (
            <>
              {" "}
              <When value={waitSkipped.skippedAt} />
            </>
          )}
          . It stays in the session.
        </p>
      )}
      <LabelBody
        file={file}
        label={stateLabel}
        run={run}
        runKnown={runKnown}
        ingest={ingest}
      />
    </>
  );
}

type IngestMutation = ReturnType<typeof useIngestFiles>;

function LabelBody({
  file,
  label,
  run,
  runKnown,
  ingest,
}: {
  file: LabelledFile;
  label: IngestionLabel;
  run: IngestionRun | null;
  runKnown: boolean;
  ingest: IngestMutation;
}) {
  switch (label) {
    case IngestionLabel.Ingested:
      return <p>Its signals can be plotted and searched.</p>;
    case IngestionLabel.StoredAsIs:
      return <StoredAsIs file={file} ingest={ingest} />;
    case IngestionLabel.AwaitingIngestion:
      return (
        <Awaiting
          file={file}
          neverRan={runKnown && run === null}
          ingest={ingest}
        />
      );
    case IngestionLabel.Ingesting:
      return (
        <>
          <p>
            Roboto is ingesting this file now.
            {run !== null && (
              <>
                {" "}
                Started <When value={run.started} />.
              </>
            )}
          </p>
          <p className={styles.note}>{INGESTED_MEANS}</p>
          <Actions>
            <RunLink run={run} />
          </Actions>
        </>
      );
    case IngestionLabel.IngestionFailed:
      return <Failed file={file} run={run} ingest={ingest} />;
    case IngestionLabel.PartlyIngested:
      return (
        <>
          <p>{PARTLY_INGESTED}</p>
          <Actions>
            <RunLink run={run} />
          </Actions>
        </>
      );
    case IngestionLabel.NotWaitedFor:
      return null;
  }
}

function Failed({
  file,
  run,
  ingest,
}: {
  file: LabelledFile;
  run: IngestionRun | null;
  ingest: IngestMutation;
}) {
  return (
    <>
      {run?.status === "finished" ? (
        <p>The ingestion action finished but didn&rsquo;t ingest this file.</p>
      ) : (
        <p>The last ingestion run failed.</p>
      )}
      {run !== null && run.detail !== null && run.detail !== "" && (
        <p data-testid="ingestion-label-detail">{run.detail}</p>
      )}
      {file.ingestion_status === IngestionStatus.PartlyIngested && (
        <p>{PARTLY_INGESTED}</p>
      )}
      <p className={styles.note}>{INGESTED_MEANS}</p>
      <Actions>
        <RunLink run={run} />
        <IngestButton file={file} ingest={ingest}>
          Run again
        </IngestButton>
      </Actions>
    </>
  );
}

function Awaiting({
  file,
  neverRan,
  ingest,
}: {
  file: LabelledFile;
  /** No rule's trigger has ever run on the file. */
  neverRan: boolean;
  ingest: IngestMutation;
}) {
  const matching = useMatchingRules(file);
  const rules = matching.data ?? [];
  const autoRule = rules.find(autoIngests);
  const pastGrace =
    file.created === undefined ||
    Date.now() - Date.parse(file.created) > FIRST_RUN_GRACE_MS;
  const stalled = autoRule !== undefined && neverRan && pastGrace;
  return (
    <>
      <p>{awaitingSummary(autoRule !== undefined, stalled)}</p>
      {file.created !== undefined && (
        <p>
          Uploaded <When value={file.created} />.
        </p>
      )}
      {stalled && (
        <>
          <p data-testid="ingestion-label-never-ran">
            The rule <RuleLink rule={autoRule} /> matches it, but no ingestion
            has started on it.
          </p>
          <Actions>
            <IngestButton file={file} ingest={ingest}>
              Ingest this file now
            </IngestButton>
          </Actions>
        </>
      )}
      {matching.isSuccess && rules.length === 0 && (
        <p data-testid="ingestion-label-nothing-will-ingest">
          No ingestion rule matches this file now, so Roboto won&rsquo;t ingest
          it on its own.
        </p>
      )}
      {matching.isSuccess && rules.length > 0 && autoRule === undefined && (
        <p data-testid="ingestion-label-nothing-will-ingest">
          Auto-ingest is off on <RuleNames rules={rules} />, so Roboto
          won&rsquo;t ingest it on its own.
        </p>
      )}
      <MatchingError matching={matching} />
      <p className={styles.note}>{INGESTED_MEANS}</p>
    </>
  );
}

/**
 * Says Roboto is waiting only when a rule that auto-ingests matches the file;
 * otherwise, or while that is not yet known, only what the record says.
 */
function awaitingSummary(autoIngestRuleMatches: boolean, stalled: boolean) {
  if (stalled) {
    return "Roboto hasn’t started ingesting this file.";
  }
  return autoIngestRuleMatches
    ? "Roboto is waiting to ingest this file."
    : "This file is marked for ingestion but isn’t ingested yet.";
}

function StoredAsIs({
  file,
  ingest,
}: {
  file: LabelledFile;
  ingest: IngestMutation;
}) {
  const matching = useMatchingRules(file);
  const rules = matching.data ?? [];
  const autoRule = rules.find(autoIngests);
  return (
    <>
      <p>
        This file isn&rsquo;t processed, so you can&rsquo;t plot its signals.
        You can still download it.
      </p>
      {matching.isLoading && <Spinner />}
      <MatchingError matching={matching} />
      {autoRule !== undefined && (
        <>
          <p data-testid="ingestion-label-rule-matches">
            The rule <RuleLink rule={autoRule} /> matches this file, but the
            file was uploaded before the rule existed.
          </p>
          <Actions>
            <IngestButton file={file} ingest={ingest}>
              Ingest this file now
            </IngestButton>
          </Actions>
        </>
      )}
      {matching.isSuccess && rules.length > 0 && autoRule === undefined && (
        <p data-testid="ingestion-label-nothing-will-ingest">
          <RuleNames rules={rules} /> {rules.length === 1 ? "matches" : "match"}{" "}
          this file, but auto-ingest is off, so Roboto doesn&rsquo;t ingest it.
        </p>
      )}
      {matching.isSuccess && rules.length === 0 && (
        <NoRuleMatches file={file} />
      )}
    </>
  );
}

/**
 * The rules that match the file. Mounted, and so fetched, only once the popup
 * is open, and never for a viewer outside the file's org.
 */
function useMatchingRules(file: LabelledFile) {
  const isMember = useIsOrgMember(file.org_id);
  return useIngestionRulesMatching(file.relative_path, {
    enabled: isMember,
    orgId: file.org_id,
  });
}

function autoIngests(rule: IngestionRuleRecord): boolean {
  return rule.auto_ingest && rule.invocation !== null;
}

function MatchingError({
  matching,
}: {
  matching: ReturnType<typeof useMatchingRules>;
}) {
  return matching.isError ? (
    <p className={styles.error} role="alert">
      {userFacingErrorMessage(matching.error)}
    </p>
  ) : null;
}

function RuleLink({ rule }: { rule: IngestionRuleRecord }) {
  return (
    <Link to={Goto.createIngestionRuleRoute(rule.rule_id)}>{rule.name}</Link>
  );
}

/** "the rule A", or "the rules A and B" / "A, B and C". */
function RuleNames({ rules }: { rules: IngestionRuleRecord[] }) {
  return (
    <>
      {rules.length === 1 ? "the rule " : "the rules "}
      {rules.map((rule, index) => (
        <React.Fragment key={rule.rule_id}>
          {index > 0 && (index === rules.length - 1 ? " and " : ", ")}
          <RuleLink rule={rule} />
        </React.Fragment>
      ))}
    </>
  );
}

function RunLink({ run }: { run: IngestionRun | null }) {
  return run !== null && run.invocation_id !== null ? (
    <InvocationLink invocationId={run.invocation_id} />
  ) : null;
}

function InvocationLink({ invocationId }: { invocationId: string }) {
  return (
    <Link
      to={`${Goto.route.Invocations}/${invocationId}`}
      data-testid="ingestion-label-run-link"
    >
      View run
    </Link>
  );
}

function Actions({ children }: React.PropsWithChildren) {
  return <div className={styles.actions}>{children}</div>;
}

/** "3 minutes ago", or "on <date>" past a day. */
function When({ value }: { value: string }) {
  return isRecent(value) ? (
    <>{formatRelativeTime(value)}</>
  ) : (
    <>
      on <FormattedDateTime value={value} />
    </>
  );
}

/**
 * Ingests the file with the rule that matches it: "Ingest this file now" for
 * a file uploaded before the rule, "Run again" after a failed run. Needs edit
 * permission on the file, which the server checks, and membership of its org.
 */
function IngestButton({
  file,
  ingest,
  children,
}: React.PropsWithChildren<{ file: LabelledFile; ingest: IngestMutation }>) {
  const isMember = useIsOrgMember(file.org_id);
  const fileId = file.file_id;
  if (fileId === undefined || !isMember) {
    return null;
  }
  const result = ingest.data?.find((entry) => entry.file_id === fileId);
  return (
    <>
      <RobotoButton
        variant="secondary"
        size="sm"
        loading={ingest.isPending}
        onClick={() => ingest.mutate([fileId])}
        data-testid="ingestion-label-ingest"
      >
        {children}
      </RobotoButton>
      {result !== undefined && (
        <p role="status" data-testid="ingestion-label-ingest-outcome">
          {INGEST_FILE_OUTCOME_MESSAGES[result.outcome]}
          {result.outcome === "started" && result.invocation_id !== null && (
            <>
              {" "}
              <InvocationLink invocationId={result.invocation_id} />
            </>
          )}
        </p>
      )}
      {ingest.isError && (
        <p
          className={styles.error}
          role="alert"
          data-testid="ingestion-label-ingest-outcome"
        >
          {ingest.error instanceof RobotoUnauthorizedException
            ? "You need permission to edit this file to ingest it."
            : userFacingErrorMessage(ingest.error)}
        </p>
      )}
    </>
  );
}

/**
 * For a file no rule matches, the button that adds one, whose label says that
 * none exists yet. Only org admins can write rules, and only in the org they
 * are working in; anyone else is told in a sentence that no rule matches.
 */
function NoRuleMatches({ file }: { file: LabelledFile }) {
  const isAdmin = useHasOrgRole("admin", { orgId: file.org_id });
  const inFileOrg = useCurrentOrg()?.org_id === file.org_id;
  if (inFileOrg && isAdmin) {
    return <AddRule file={file} />;
  }
  return (
    <p data-testid="ingestion-label-no-rule">
      {inFileOrg
        ? "No ingestion rule matches this file; an org admin can add one."
        : "No ingestion rule matches this file."}
    </p>
  );
}

/**
 * A rule for every file of this one's kind. When Roboto has a default rule
 * for the kind (`**\/*.ulg` → `ulog_ingestion`), the new rule starts from it,
 * action included.
 */
function rulePrefillWithDefaults(
  relativePath: string,
  defaults: readonly DefaultIngestionRule[],
): IngestionRulePrefill {
  const prefill = rulePrefillForFile(relativePath);
  const [pattern] = prefill.includePatterns;
  const match = defaults.find((rule) =>
    rule.include_patterns.some(
      (include) => include.toLowerCase() === pattern?.toLowerCase(),
    ),
  );
  if (match === undefined) {
    return prefill;
  }
  return {
    name: match.name,
    includePatterns: [...match.include_patterns],
    excludePatterns: [...match.exclude_patterns],
    invocation: match.invocation,
  };
}

function AddRule({ file }: { file: LabelledFile }) {
  const navigate = useNavigate();
  const defaults = useDefaultIngestionRules(file.org_id);
  return (
    <>
      <Actions>
        <RobotoButton
          variant="secondary"
          size="sm"
          icon={<Plus aria-hidden="true" />}
          loading={defaults.isLoading}
          onClick={() =>
            void navigate(
              Goto.createNewIngestionRuleRoute(
                rulePrefillWithDefaults(
                  file.relative_path,
                  defaults.data ?? [],
                ),
              ),
            )
          }
          data-testid="ingestion-label-add-rule"
        >
          Add an ingestion rule for{" "}
          <code className={styles.pattern}>
            {filePatternLabel(file.relative_path)}
          </code>
          {fileExtension(file.relative_path) !== null && " files"}
        </RobotoButton>
      </Actions>
      <p className={styles.note}>
        After you save the rule, you can also ingest the matching files already
        uploaded.
      </p>
    </>
  );
}
